import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { CAMPAIGN_CHANNELS } from '#/entities/campaign'
import type { CampaignChannel } from '#/entities/campaign'
import { cn } from '#/shared/lib/utils'
import { Button } from '#/shared/ui/button'

import {
  CAMPAIGN_ADD_STEP_PHASES,
  CAMPAIGN_STEP_DELAYS,
  CAMPAIGN_STEP_CONDITION_ORDER,
  CAMPAIGN_STEP_DELAY_ORDER,
} from '../model/use-campaign-add-step'
import type { UseCampaignAddStepResult } from '../model/use-campaign-add-step'
import { CampaignChoiceChip as Chip } from './campaign-choice-chip'
import { CampaignCustomDaysInput } from './campaign-custom-days-input'

const CHANNEL_ORDER: readonly CampaignChannel[] = [
  CAMPAIGN_CHANNELS.sms,
  CAMPAIGN_CHANNELS.email,
  CAMPAIGN_CHANNELS.task,
]

interface CampaignAddStepPickerProps {
  state: UseCampaignAddStepResult
  /**
   * "Step 5 · SMS · 2 days after step 4 · 164 eligible", dry-run against the
   * campaign. Absent while it is in flight or when the choice cannot be
   * previewed.
   */
  summary?: string
}

/** Collapsed resting state — the row that opens the picker. */
function AddStepRow({ onOpen }: { onOpen: () => void }) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full cursor-pointer items-center justify-center gap-2.5 rounded-xl border border-dashed border-gray-200 bg-white py-4 hover:bg-gray-50"
    >
      <span className="text-sm font-medium text-campaign-running">
        {t('campaigns.sequence.addStep.open')}
      </span>
      <span className="text-xs text-gray-300">
        {t('campaigns.sequence.addStep.openHint')}
      </span>
    </button>
  )
}

function ChannelOptions({ state }: CampaignAddStepPickerProps) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-2 sm:flex-row">
      {CHANNEL_ORDER.map((channel) => {
        const selected = state.channel === channel
        return (
          <button
            key={channel}
            type="button"
            aria-pressed={selected}
            onClick={() => state.onChannelChange(channel)}
            // The selected edge is a 1px border plus an inset ring rather than
            // a 2px border: doubling the border would re-lay the box out and
            // nudge every option sideways on each click.
            className={cn(
              'flex flex-1 cursor-pointer flex-col items-start gap-1 rounded-lg border p-3 text-left transition-colors',
              selected
                ? 'border-campaign-running bg-avatar-blue-soft ring-1 ring-campaign-running ring-inset'
                : 'border-gray-200 bg-white hover:bg-gray-50',
            )}
          >
            <span className="text-sm font-medium text-campaign-running">
              {t(`campaigns.channel.${channel}`)}
            </span>
            <span className="text-xs text-warm-500">
              {t(`campaigns.sequence.addStep.channelHint.${channel}`)}
            </span>
          </button>
        )
      })}
    </div>
  )
}

function PickerFooter({ state, summary }: CampaignAddStepPickerProps) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-wrap items-center gap-2.5 pt-2.5">
      {/* The service's own sentence for the step being described. It keeps its
          place in the row while it resolves, so settling a chip does not shunt
          the buttons sideways. */}
      <p className="min-w-0 flex-1 text-xs text-warm-500">{summary}</p>
      <Button variant="outline" size="sm" onClick={state.onClose}>
        {t('campaigns.sequence.addStep.cancel')}
      </Button>
      <Button
        size="sm"
        pressEffect
        // Custom with no valid day count has no delay to write the step on.
        disabled={state.delayMinutes == null}
        onClick={state.onCompose}
      >
        {t('campaigns.sequence.addStep.write')}
      </Button>
    </div>
  )
}

export function CampaignAddStepPicker({
  state,
  summary,
}: CampaignAddStepPickerProps) {
  const { t } = useTranslation()
  const ref = useRef<HTMLElement>(null)
  // The header's "Add step" opens this from the top of the page, and with a
  // few steps above it the picker would expand off-screen.
  useEffect(() => {
    if (state.phase !== CAMPAIGN_ADD_STEP_PHASES.picking) return
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ref.current?.scrollIntoView({
      behavior: reduce ? 'auto' : 'smooth',
      block: 'center',
    })
  }, [state.phase])
  // The composer replaces this block entirely — the tab renders it, so the
  // "add a step" affordance must not sit under an open composer.
  if (state.phase === CAMPAIGN_ADD_STEP_PHASES.composing) return null
  if (state.phase === CAMPAIGN_ADD_STEP_PHASES.closed) {
    return <AddStepRow onOpen={state.onOpen} />
  }

  return (
    <section
      ref={ref}
      className="flex flex-col gap-2 rounded-xl border border-gray-200 bg-white p-3.5"
    >
      <p className="text-xs text-warm-500">
        {t('campaigns.sequence.addStep.intro')}
      </p>

      <ChannelOptions state={state} />

      <div className="flex flex-wrap items-center gap-1.5 pt-1">
        <span className="text-xs text-warm-500">
          {t('campaigns.sequence.addStep.when')}
        </span>
        {CAMPAIGN_STEP_DELAY_ORDER.map((delay) => (
          <Chip
            key={delay}
            label={t(`campaigns.sequence.addStep.delay.${delay}`)}
            selected={state.delay === delay}
            onClick={() => state.onDelayChange(delay)}
          />
        ))}
        {state.delay === CAMPAIGN_STEP_DELAYS.custom && (
          <CampaignCustomDaysInput
            value={state.customDays}
            onChange={state.onCustomDaysChange}
            invalid={state.customDays !== '' && state.delayMinutes == null}
          />
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5 pt-1">
        <span className="text-xs text-warm-500">
          {t('campaigns.sequence.addStep.onlyIf')}
        </span>
        {CAMPAIGN_STEP_CONDITION_ORDER.map((condition) => (
          <Chip
            key={condition}
            label={t(`campaigns.sequence.addStep.condition.${condition}`)}
            selected={state.conditions.includes(condition)}
            onClick={() => state.toggleCondition(condition)}
          />
        ))}
        <Chip
          label={t('campaigns.sequence.addStep.alwaysSend')}
          selected={state.alwaysSend}
          onClick={state.setAlwaysSend}
        />
      </div>

      <p className="pt-1.5 text-xs text-warm-500">
        {t('campaigns.sequence.addStep.resolvedNote')}
      </p>

      <PickerFooter state={state} summary={summary} />
    </section>
  )
}
